/**
 * AI Results Drawer
 * Extends theme's Drawer class for AI search results
 */

if (!customElements.get('ai-results-drawer')) {
  customElements.define('ai-results-drawer', class AIResultsDrawer extends Drawer {
    constructor() {
      super();
      
      // Cache DOM elements
      this.titleElement = this.querySelector('#ai-drawer-title');
      this.loadingState = this.querySelector('.ai-drawer-loading');
      this.contentContainer = this.querySelector('#ai-drawer-content');
      this.errorState = this.querySelector('.ai-drawer-error');
      this.retryButton = this.querySelector('.ai-drawer-retry');
      this.overlay = this.querySelector('.modal-overlay');
      
      // State
      this.currentUrl = null;
      
      // Bind events
      this._bindEvents();
      
      console.log('✅ AI Results Drawer: Initialized');
    }
    
    _bindEvents() {
      // Retry button
      if (this.retryButton) {
        this.retryButton.addEventListener('click', () => {
          if (this.currentUrl) {
            this.loadContent(this.currentUrl);
          }
        });
      }
      
      // Overlay click to close
      if (this.overlay) {
        this.overlay.addEventListener('click', (e) => {
          e.stopPropagation();
          this.hide();
          console.log('🎨 AI Results Drawer: Closed via overlay click');
        });
      }
    }
    
    /**
     * Show drawer with optional title
     */
    show(opener, title = 'Search Results') {
      super.show(opener);
      
      // Update title
      if (this.titleElement) {
        this.titleElement.textContent = title;
      }
      
      console.log('🎨 AI Results Drawer: Opened');
    }
    
    /**
     * Hide drawer and clear content
     */
    hide() {
      super.hide();
      
      // Clear content after animation
      setTimeout(() => {
        if (!this.hasAttribute('open')) {
          this.contentContainer.innerHTML = '';
          this.currentUrl = null;
        }
      }, 300);
      
      console.log('🎨 AI Results Drawer: Closed');
    }
    
    /**
     * Load collection grid via AJAX
     */
    async loadContent(url, title = 'Search Results') {
      this.currentUrl = url;
      
      // Show drawer if not open
      if (!this.hasAttribute('open')) {
        this.show(null, title);
      }
      
      // Show loading state
      this._showLoading();
      
      try {
        console.log('🎨 AI Results Drawer: Loading URL:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const html = await response.text();
        
        // Hide loading, show content
        this._showContent(html);
        
        // Scroll to top
        const drawerContent = this.querySelector('.drawer-content');
        if (drawerContent) {
          drawerContent.scrollTop = 0;
        }
        
        // Re-initialize Shopify scripts
        this._initLoadedContent();
        
      // Setup infinite scroll instead of pagination
      this._setupInfiniteScroll();
      
      // Force 2-column grid
      this._force2ColumnGrid();
      
      console.log('✅ AI Results Drawer: Content loaded');
        
      } catch (error) {
        console.error('❌ AI Results Drawer: Load failed:', error);
        this._showError();
      }
    }
    
    /**
     * Show loading state
     */
    _showLoading() {
      this.loadingState.style.display = 'block';
      this.contentContainer.style.display = 'none';
      this.errorState.style.display = 'none';
    }
    
    /**
     * Show content
     */
    _showContent(html) {
      this.loadingState.style.display = 'none';
      this.contentContainer.style.display = 'block';
      this.errorState.style.display = 'none';
      this.contentContainer.innerHTML = html;
    }
    
    /**
     * Show error state
     */
    _showError() {
      this.loadingState.style.display = 'none';
      this.contentContainer.style.display = 'none';
      this.errorState.style.display = 'block';
    }
    
    /**
     * Re-initialize Shopify scripts in loaded content
     */
    _initLoadedContent() {
      // Shopify Payment Button
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }
      
      // Dispatch custom event
      const event = new CustomEvent('ai-drawer:contentLoaded', {
        detail: { content: this.contentContainer }
      });
      document.dispatchEvent(event);
      
      // Quick-add buttons - ensure they're initialized
      const quickAddButtons = this.contentContainer.querySelectorAll('[data-quick-add], quick-add-button');
      if (quickAddButtons.length > 0) {
        // Initialize theme quick-add if available
        if (window.theme && window.theme.initQuickAdd) {
          window.theme.initQuickAdd();
        }
        
        // Also ensure custom elements are connected
        quickAddButtons.forEach(btn => {
          if (btn.tagName === 'QUICK-ADD-BUTTON' && btn.connectedCallback) {
            btn.connectedCallback();
          }
        });
      }
      
      // Variant selectors - ensure they work
      const variantSelectors = this.contentContainer.querySelectorAll('variant-selects, variant-radios');
      variantSelectors.forEach(selector => {
        if (selector.connectedCallback) {
          selector.connectedCallback();
        }
      });
      
      // Product forms - ensure they submit correctly
      const productForms = this.contentContainer.querySelectorAll('product-form');
      productForms.forEach(form => {
        if (form.connectedCallback) {
          form.connectedCallback();
        }
      });
      
      // Add data attributes for product type styling
      this._addProductTypeAttributes();
      
      console.log(`✅ Initialized: ${quickAddButtons.length} quick-add, ${variantSelectors.length} variant selectors, ${productForms.length} product forms`);
    }
    
    /**
     * Add data attributes to product cards for styling based on type/tags
     */
    _addProductTypeAttributes() {
      const productCards = this.contentContainer.querySelectorAll('.card-wrapper, .product-card, .grid__item');
      
      productCards.forEach(card => {
        // Try to find product type from various sources
        const productLink = card.querySelector('a[href*="/products/"]');
        const productTitle = card.querySelector('.card__heading, .card-information__text');
        const productTags = card.querySelector('[data-tags]');
        
        if (productLink) {
          const href = productLink.getAttribute('href') || '';
          const title = (productTitle?.textContent || '').toLowerCase();
          const tags = (productTags?.getAttribute('data-tags') || '').toLowerCase();
          
          // Check for peelable products
          if (href.includes('peelable') || title.includes('peelable') || 
              title.includes('plasti dip') || title.includes('hyperdip') ||
              tags.includes('peelable')) {
            card.setAttribute('data-product-type', 'peelable');
          }
          // Check for permanent/Luna products
          else if (href.includes('luna') || href.includes('permanent') ||
                   title.includes('luna') || title.includes('permanent') ||
                   title.includes('vhs') || title.includes('uhs') ||
                   tags.includes('permanent') || tags.includes('luna')) {
            card.setAttribute('data-product-type', 'permanent');
          }
        }
      });
      
      console.log(`🎨 Added product type attributes to ${productCards.length} cards`);
    }
    
    /**
     * Setup infinite scroll
     */
    _setupInfiniteScroll() {
      // Find next page link
      const nextPageLink = this.contentContainer.querySelector('a[href*="page="]');
      
      if (!nextPageLink) {
        console.log('🎨 AI Results Drawer: No more pages to load');
        return;
      }
      
      const nextPageUrl = nextPageLink.getAttribute('href');
      
      // Create intersection observer for infinite scroll
      const drawerContent = this.querySelector('.drawer-content');
      if (!drawerContent) {
        console.log('⚠️ AI Results Drawer: No drawer-content found');
        return;
      }
      
      // Remove existing observer if any
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
      }
      
      // Find the product grid to append sentinel after it
      const productGrid = this.contentContainer.querySelector('.grid, .product-grid, ul.content-list');
      if (!productGrid) {
        console.log('⚠️ AI Results Drawer: No product grid found');
        return;
      }
      
      // Create a sentinel element at the bottom of the grid
      let sentinel = this.contentContainer.querySelector('.scroll-sentinel');
      if (!sentinel) {
        sentinel = document.createElement('div');
        sentinel.className = 'scroll-sentinel';
        sentinel.style.height = '20px';
        sentinel.style.width = '100%';
        // Insert after the grid
        productGrid.parentNode.insertBefore(sentinel, productGrid.nextSibling);
      }
      
      // Observe when sentinel comes into view - use null root for viewport
      this.scrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && nextPageUrl && !this.isLoadingMore) {
            console.log('🎨 AI Results Drawer: Sentinel visible, loading more...');
            this.isLoadingMore = true;
            this._loadMoreProducts(nextPageUrl);
          }
        });
      }, {
        root: null, // Use viewport instead of drawer-content
        rootMargin: '400px', // Start loading 400px before reaching bottom
        threshold: 0.01
      });
      
      this.scrollObserver.observe(sentinel);
      console.log('🎨 AI Results Drawer: Infinite scroll enabled for:', nextPageUrl);
    }
    
    /**
     * Load more products (infinite scroll)
     */
    async _loadMoreProducts(url) {
      try {
        console.log('🎨 AI Results Drawer: Loading more products...');
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract product grid
        const newProducts = doc.querySelector('.grid, .product-grid, ul.content-list');
        if (newProducts) {
          const existingGrid = this.contentContainer.querySelector('.grid, .product-grid, ul.content-list');
          if (existingGrid) {
            // Append new products
            Array.from(newProducts.children).forEach(child => {
              existingGrid.appendChild(child);
            });
            
            // Re-initialize for new products
            this._initLoadedContent();
            this._force2ColumnGrid();
            
            // Setup next page
            this._setupInfiniteScroll();
            
            console.log('✅ AI Results Drawer: More products loaded');
          }
        }
        
        this.isLoadingMore = false;
        
      } catch (error) {
        console.error('❌ AI Results Drawer: Failed to load more:', error);
        this.isLoadingMore = false;
      }
    }
    
    /**
     * Force 2-column grid layout
     */
    _force2ColumnGrid() {
      // Target the actual grid classes used in collections - search in contentContainer!
      const grids = this.contentContainer.querySelectorAll('.ai-collection-products ul.grid, .content-list.auto-grid, ul.content-list, .grid, .product-grid');
      grids.forEach(grid => {
        if (grid.style) {
          grid.style.display = 'grid';
          grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
          grid.style.gap = '1rem';
          grid.style.setProperty('--item-min-size', 'auto');
          grid.style.setProperty('--item-min-size-mobile', 'auto');
          grid.style.listStyleType = 'none';
          grid.style.listStyle = 'none';
          grid.style.paddingLeft = '0';
          grid.style.paddingInlineStart = '0';
        }
      });
      
      // Remove list markers from all list items
      const listItems = this.contentContainer.querySelectorAll('li, .grid-item, .grid__item');
      listItems.forEach(item => {
        if (item.style) {
          item.style.listStyleType = 'none';
          item.style.listStyle = 'none';
        }
      });
      
      console.log(`🎨 AI Results Drawer: Forced ${grids.length} grids to 2 columns, removed markers from ${listItems.length} items`);
    }
  });
}

// Global helper for easy access
window.DYC = window.DYC || {};
window.DYC.AIDrawer = {
  open: function(url, title) {
    const drawer = document.getElementById('AI-Results-Drawer');
    if (drawer && drawer.loadContent) {
      drawer.loadContent(url, title);
    } else {
      console.error('AI Results Drawer not found');
      // Fallback: redirect
      window.location.href = url.replace(/[?&]view=grid-only/, '');
    }
  },
  
  close: function() {
    const drawer = document.getElementById('AI-Results-Drawer');
    if (drawer && drawer.hide) {
      drawer.hide();
    }
  }
};
